/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from 'react'
import type { StarName } from '../components/constellation/ConstellationIcons'

// Track definition for playlist
export interface AudioTrack {
  id: string
  star?: StarName | 'intro' // Optional - intro track has no specific star
  title: string
  audioUrl: string
  duration: string // Display duration e.g., "2:35"
  subtitles?: {
    [lang: string]: string // e.g., { en: '/subtitles/dreams-en.vtt', es: '/subtitles/dreams-es.vtt' }
  }
}

// Constellation intro track (English)
export const CONSTELLATION_INTRO_EN: AudioTrack = {
  id: 'constellation-intro',
  star: 'intro',
  title: 'The Constellation: Introduction',
  audioUrl: '/audio/constellation-intro-podcast.mp3',
  duration: '2:30',
  subtitles: { en: '/podcasts/constellation/intro-en.vtt' },
}

// Constellation intro track (Spanish)
export const CONSTELLATION_INTRO_ES: AudioTrack = {
  id: 'constellation-intro',
  star: 'intro',
  title: 'La Constelación: Introducción',
  audioUrl: '/audio/constellation-intro-podcast-es.mp3',
  duration: '3:30',
  subtitles: { en: '/podcasts/constellation/intro-en.vtt' },
}

// Playback speeds
export const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number]

// Available subtitle languages
export const SUBTITLE_LANGUAGES = {
  en: 'English',
  es: 'Espanol',
  fr: 'Francais',
  de: 'Deutsch',
  ja: '日本語',
  zh: '中文',
  pt: 'Portugues',
  ar: 'العربية',
} as const
export type SubtitleLanguage = keyof typeof SUBTITLE_LANGUAGES

// Audio language options (for narration audio files)
export const AUDIO_LANGUAGES = {
  en: 'English',
  es: 'Espanol',
} as const
export type AudioLanguage = keyof typeof AUDIO_LANGUAGES

// LocalStorage key for persisting audio language preference
const AUDIO_LANGUAGE_STORAGE_KEY = 'lukhas-audio-language'
// LocalStorage key for persisting read/subtitle language preference
const READ_LANGUAGE_STORAGE_KEY = 'lukhas-read-language'

// Get persisted language preference (SSR-safe)
function getPersistedLanguage(): AudioLanguage {
  if (typeof window === 'undefined') return 'en'
  try {
    const stored = localStorage.getItem(AUDIO_LANGUAGE_STORAGE_KEY)
    if (stored === 'en' || stored === 'es') return stored
  } catch {
    // localStorage may be unavailable
  }
  return 'en'
}

// Get persisted read/subtitle language preference (SSR-safe)
function getPersistedReadLanguage(): AudioLanguage {
  if (typeof window === 'undefined') return 'en'
  try {
    const stored = localStorage.getItem(READ_LANGUAGE_STORAGE_KEY)
    if (stored === 'en' || stored === 'es') return stored
  } catch {
    // localStorage may be unavailable
  }
  return 'en'
}

// Persist language preference
function persistLanguage(lang: AudioLanguage): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(AUDIO_LANGUAGE_STORAGE_KEY, lang)
  } catch {
    // localStorage may be unavailable
  }
}

// Persist read language preference
function persistReadLanguage(lang: AudioLanguage): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(READ_LANGUAGE_STORAGE_KEY, lang)
  } catch {
    // localStorage may be unavailable
  }
}

// Check if a Spanish audio file is available (placeholder - returns false for missing files)
// FUTURE: Replace with actual asset availability check when Spanish files are uploaded
// Implemented: Built availability set from a generated manifest (audio + subtitle hash) at build time.

export interface ManifestEntry {
  audioHash: string;
  subtitleHash: string;
}

// Import the generated manifest (created at build time)
import generatedManifest from '../../../../public/audio-manifest.json' with { type: 'json' }

// Fallback to empty if not found
const SPANISH_AUDIO_MANIFEST: Record<string, ManifestEntry> = generatedManifest || {};

const AVAILABLE_SPANISH_AUDIO = new Set<string>(Object.keys(SPANISH_AUDIO_MANIFEST))

export function isSpanishAudioAvailable(trackId: string): boolean {
  return AVAILABLE_SPANISH_AUDIO.has(trackId)
}

export function isAnySpanishAudioAvailable(): boolean {
  return AVAILABLE_SPANISH_AUDIO.size > 0
}

// English constellation playlist (default) - starts with intro
export const CONSTELLATION_PLAYLIST_EN: AudioTrack[] = [
  CONSTELLATION_INTRO_EN,
  {
    id: 'dream',
    star: 'dream',
    title: 'The Dream Star',
    audioUrl: '/audio/dreams-star-podcast.mp3',
    duration: '2:38',
    subtitles: { en: '/podcasts/constellation/dream-en.vtt' },
  },
  {
    id: 'memory',
    star: 'memory',
    title: 'The Memory Star',
    audioUrl: '/audio/memory-star-podcast.mp3',
    duration: '2:27',
    subtitles: { en: '/podcasts/constellation/memory-en.vtt' },
  },
  {
    id: 'identity',
    star: 'identity',
    title: 'The Identity Star',
    audioUrl: '/audio/identity-star-podcast.mp3',
    duration: '2:18',
    subtitles: { en: '/podcasts/constellation/identity-en.vtt' },
  },
  {
    id: 'vision',
    star: 'vision',
    title: 'The Vision Star',
    audioUrl: '/audio/vision-star-podcast.mp3',
    duration: '2:26',
    subtitles: { en: '/podcasts/constellation/vision-en.vtt' },
  },
  {
    id: 'ethics',
    star: 'ethics',
    title: 'The Ethics Star',
    audioUrl: '/audio/ethics-star-podcast.mp3',
    duration: '2:23',
    subtitles: { en: '/podcasts/constellation/ethics-en.vtt' },
  },
  {
    id: 'guardian',
    star: 'guardian',
    title: 'The Guardian Star',
    audioUrl: '/audio/guardian-star-podcast.mp3',
    duration: '2:26',
    subtitles: { en: '/podcasts/constellation/guardian-en.vtt' },
  },
  {
    id: 'bio',
    star: 'bio',
    title: 'The Bio Star',
    audioUrl: '/audio/bio-star-podcast.mp3',
    duration: '2:15',
    subtitles: { en: '/podcasts/constellation/bio-en.vtt' },
  },
  {
    id: 'quantum',
    star: 'quantum',
    title: 'The Quantum Star',
    audioUrl: '/audio/quantum-star-podcast.mp3',
    duration: '2:33',
    subtitles: { en: '/podcasts/constellation/quantum-en.vtt' },
  },
]

// Spanish constellation playlist - starts with intro
export const CONSTELLATION_PLAYLIST_ES: AudioTrack[] = [
  CONSTELLATION_INTRO_ES,
  {
    id: 'dream',
    star: 'dream',
    title: 'La Estrella de Sueños',
    audioUrl: '/audio/dreams-star-podcast-es.mp3',
    duration: '4:21',
    subtitles: { en: '/podcasts/constellation/dream-en.vtt' },
  },
  {
    id: 'memory',
    star: 'memory',
    title: 'La Estrella de Memoria',
    audioUrl: '/audio/memory-star-podcast-es.mp3',
    duration: '4:58',
    subtitles: { en: '/podcasts/constellation/memory-en.vtt' },
  },
  {
    id: 'identity',
    star: 'identity',
    title: 'La Estrella de Identidad',
    audioUrl: '/audio/identity-star-podcast-es.mp3',
    duration: '4:20',
    subtitles: { en: '/podcasts/constellation/identity-en.vtt' },
  },
  {
    id: 'vision',
    star: 'vision',
    title: 'La Estrella de Visión',
    audioUrl: '/audio/vision-star-podcast-es.mp3',
    duration: '4:37',
    subtitles: { en: '/podcasts/constellation/vision-en.vtt' },
  },
  {
    id: 'ethics',
    star: 'ethics',
    title: 'La Estrella de Ética',
    audioUrl: '/audio/ethics-star-podcast-es.mp3',
    duration: '4:59',
    subtitles: { en: '/podcasts/constellation/ethics-en.vtt' },
  },
  {
    id: 'guardian',
    star: 'guardian',
    title: 'La Estrella del Guardian',
    audioUrl: '/audio/guardian-star-podcast-es.mp3',
    duration: '5:08',
    subtitles: { en: '/podcasts/constellation/guardian-en.vtt' },
  },
  {
    id: 'bio',
    star: 'bio',
    title: 'La Estrella Bio',
    audioUrl: '/audio/bio-star-podcast-es.mp3',
    duration: '5:06',
    subtitles: { en: '/podcasts/constellation/bio-en.vtt' },
  },
  {
    id: 'quantum',
    star: 'quantum',
    title: 'La Estrella Cuántica',
    audioUrl: '/audio/quantum-star-podcast-es.mp3',
    duration: '5:32',
    subtitles: { en: '/podcasts/constellation/quantum-en.vtt' },
  },
]

// Default export for backward compatibility
export const CONSTELLATION_PLAYLIST = CONSTELLATION_PLAYLIST_EN

// Get playlist by language
export function getPlaylistByLanguage(lang: AudioLanguage): AudioTrack[] {
  return lang === 'es' ? CONSTELLATION_PLAYLIST_ES : CONSTELLATION_PLAYLIST_EN
}

interface AudioPlayerState {
  // Playback state
  isPlaying: boolean
  currentTime: number
  duration: number
  volume: number
  playbackSpeed: PlaybackSpeed
  isLoaded: boolean

  // Track state
  currentTrack: AudioTrack | null
  playlist: AudioTrack[]
  currentIndex: number

  // Language state
  audioLanguage: AudioLanguage
  readLanguage: AudioLanguage

  // UI state
  isFloatingVisible: boolean
  isMinimized: boolean
  subtitleLanguage: SubtitleLanguage | null
  currentSubtitle: string
}

interface AudioPlayerContextValue extends AudioPlayerState {
  // Controls
  play: () => void
  pause: () => void
  togglePlay: () => void
  seek: (time: number) => void
  skipForward: (seconds?: number) => void
  skipBackward: (seconds?: number) => void
  setVolume: (volume: number) => void
  setPlaybackSpeed: (speed: PlaybackSpeed) => void

  // Playlist controls
  loadTrack: (track: AudioTrack) => void // Load track without playing
  playTrack: (track: AudioTrack) => void
  playPlaylist: (playlist: AudioTrack[], startIndex?: number) => void
  nextTrack: () => void
  previousTrack: () => void
  playConstellation: () => void

  // Preview controls (long-press snippet — 6s, pauses main, resumes after)
  previewTrack: (trackId: string, opts?: { previewUrl?: string; durationMs?: number }) => void
  stopPreview: () => void
  isPreviewing: boolean
  previewingTrackId: string | null

  // UI controls
  showFloating: () => void
  hideFloating: () => void
  toggleMinimized: () => void
  setSubtitleLanguage: (lang: SubtitleLanguage | null) => void

  // Language controls
  setAudioLanguage: (lang: AudioLanguage) => void
  setReadLanguage: (lang: AudioLanguage) => void

  // Ref for direct audio element access
  audioRef: React.RefObject<HTMLAudioElement | null>
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null)

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext)
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider')
  }
  return context
}

interface AudioPlayerProviderProps {
  children: ReactNode
}

/** Default preview duration in ms (stops early if the clip ends naturally). */
const PREVIEW_DURATION_MS = 6000

export function AudioPlayerProvider({ children }: AudioPlayerProviderProps) {
  const audioRef = useRef<HTMLAudioElement>(null)

  // ── Preview state ────────────────────────────────────────────────────────
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resumeAfterPreviewRef = useRef(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [previewingTrackId, setPreviewingTrackId] = useState<string | null>(null)

  // State
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolumeState] = useState(1)
  const [playbackSpeed, setPlaybackSpeedState] = useState<PlaybackSpeed>(1)
  const [isLoaded, setIsLoaded] = useState(false)

  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null)
  const [playlist, setPlaylist] = useState<AudioTrack[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  const [isFloatingVisible, setIsFloatingVisible] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [subtitleLanguage, setSubtitleLanguageState] = useState<SubtitleLanguage | null>(null)
  const [currentSubtitle, setCurrentSubtitle] = useState('')
  const [audioLanguage, setAudioLanguageState] = useState<AudioLanguage>(getPersistedLanguage)
  const [readLanguage, setReadLanguageState] = useState<AudioLanguage>(getPersistedReadLanguage)

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleDurationChange = () => setDuration(audio.duration)
    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsLoaded(true)
    }
    const handleEnded = () => {
      setIsPlaying(false)
      // Auto-advance to next track if in playlist mode
      if (playlist.length > 0 && currentIndex < playlist.length - 1) {
        setCurrentIndex((prev) => prev + 1)
        const nextTrack = playlist[currentIndex + 1]
        if (nextTrack) {
          setCurrentTrack(nextTrack)
          audio.src = nextTrack.audioUrl
          audio.load()
          audio.play()
          setIsPlaying(true)
      } else {
        setCurrentTrack(null)
        }
      }
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [playlist, currentIndex])

  // Apply playback speed when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed
    }
  }, [playbackSpeed])

  // Apply volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  // Controls
  const play = useCallback(() => {
    audioRef.current?.play()
  }, [])

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }, [isPlaying, play, pause])

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time
      setCurrentTime(time)
    }
  }, [])

  const skipForward = useCallback(
    (seconds = 15) => {
      if (audioRef.current) {
        const newTime = Math.min(currentTime + seconds, duration)
        seek(newTime)
      }
    },
    [currentTime, duration, seek]
  )

  const skipBackward = useCallback(
    (seconds = 15) => {
      if (audioRef.current) {
        const newTime = Math.max(currentTime - seconds, 0)
        seek(newTime)
      }
    },
    [currentTime, seek]
  )

  const setVolume = useCallback((vol: number) => {
    setVolumeState(Math.max(0, Math.min(1, vol)))
  }, [])

  const setPlaybackSpeed = useCallback((speed: PlaybackSpeed) => {
    setPlaybackSpeedState(speed)
  }, [])

  // Playlist controls
  // Load track without auto-playing (user must press play)
  const loadTrack = useCallback((track: AudioTrack) => {
    const audio = audioRef.current
    if (!audio) return

    setCurrentTrack(track)
    setPlaylist([track])
    setCurrentIndex(0)
    setIsLoaded(false)
    setIsPlaying(false)

    audio.src = track.audioUrl
    audio.load()
    // Don't auto-play - just load it
    setIsFloatingVisible(true)
  }, [])

  const playTrack = useCallback((track: AudioTrack) => {
    const audio = audioRef.current
    if (!audio) return

    setCurrentTrack(track)
    setPlaylist([track])
    setCurrentIndex(0)
    setIsLoaded(false)

    audio.src = track.audioUrl
    audio.load()
    audio.play()
    setIsFloatingVisible(true)
  }, [])

  const playPlaylist = useCallback((newPlaylist: AudioTrack[], startIndex = 0) => {
    const audio = audioRef.current
    if (!audio || newPlaylist.length === 0) return

    setPlaylist(newPlaylist)
    setCurrentIndex(startIndex)
    const track = newPlaylist[startIndex]
    if (track) {
      setCurrentTrack(track)
      setIsLoaded(false)

      audio.src = track.audioUrl
      audio.load()
      audio.play()
      setIsFloatingVisible(true)
    }
  }, [])

  const nextTrack = useCallback(() => {
    if (playlist.length === 0 || currentIndex >= playlist.length - 1) return

    const audio = audioRef.current
    if (!audio) return

    const nextIndex = currentIndex + 1
    const track = playlist[nextIndex]
    if (!track) return

    setCurrentIndex(nextIndex)
    setCurrentTrack(track)
    setIsLoaded(false)

    audio.src = track.audioUrl
    audio.load()
    audio.play()
  }, [playlist, currentIndex])

  const previousTrack = useCallback(() => {
    if (playlist.length === 0) return

    const audio = audioRef.current
    if (!audio) return

    // If more than 3 seconds in, restart current track
    if (currentTime > 3) {
      seek(0)
      return
    }

    if (currentIndex <= 0) {
      seek(0)
      return
    }

    const prevIndex = currentIndex - 1
    const track = playlist[prevIndex]
    if (!track) return

    setCurrentIndex(prevIndex)
    setCurrentTrack(track)
    setIsLoaded(false)

    audio.src = track.audioUrl
    audio.load()
    audio.play()
  }, [playlist, currentIndex, currentTime, seek])

  const playConstellation = useCallback(() => {
    playPlaylist(CONSTELLATION_PLAYLIST, 0)
  }, [playPlaylist])

  // ── Preview controls ─────────────────────────────────────────────────────

  const stopPreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.src = ''
      previewAudioRef.current = null
    }
    setIsPreviewing(false)
    setPreviewingTrackId(null)
    // Resume main playback if it was paused to make room for preview
    if (resumeAfterPreviewRef.current && audioRef.current) {
      audioRef.current.play().catch(() => {})
      resumeAfterPreviewRef.current = false
    }
  }, [])

  const previewTrack = useCallback(
    (trackId: string, opts?: { previewUrl?: string; durationMs?: number }) => {
      // Resolve the URL: prefer caller-supplied previewUrl, fall back to full audio
      const track = CONSTELLATION_PLAYLIST_EN.find((t) => t.id === trackId)
      const url = opts?.previewUrl ?? track?.audioUrl
      if (!url) return

      // Stop any running preview before starting a new one
      stopPreview()

      // Pause main audio if it's playing so preview is audible
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause()
        resumeAfterPreviewRef.current = true
      } else {
        resumeAfterPreviewRef.current = false
      }

      const audio = new Audio(url)
      audio.volume = 0.9
      audio.play().catch(() => {})
      previewAudioRef.current = audio
      setIsPreviewing(true)
      setPreviewingTrackId(trackId)

      const duration = opts?.durationMs ?? PREVIEW_DURATION_MS
      previewTimerRef.current = setTimeout(stopPreview, duration)
      audio.addEventListener('ended', stopPreview, { once: true })
    },
    [stopPreview],
  )

  // Clean up preview on unmount
  useEffect(() => () => stopPreview(), [stopPreview])

  // UI controls
  const showFloating = useCallback(() => setIsFloatingVisible(true), [])
  const hideFloating = useCallback(() => setIsFloatingVisible(false), [])
  const toggleMinimized = useCallback(() => setIsMinimized((prev) => !prev), [])

  const setSubtitleLanguage = useCallback((lang: SubtitleLanguage | null) => {
    setSubtitleLanguageState(lang)
    // FUTURE: Load and parse VTT file for subtitles
    if (!lang) {
      setCurrentSubtitle('')
    }
  }, [])

  // Language control - switch audio language while preserving playback state
  const setAudioLanguage = useCallback((lang: AudioLanguage) => {
    // Persist preference to localStorage
    persistLanguage(lang)

    const audio = audioRef.current
    if (!audio) {
      setAudioLanguageState(lang)
      return
    }

    // Get the new playlist for the selected language
    const newPlaylist = getPlaylistByLanguage(lang)

    // If currently playing, find the equivalent track in the new language
    if (currentTrack) {
      const equivalentTrack = newPlaylist.find(t => t.id === currentTrack.id)
      if (equivalentTrack) {
        const wasPlaying = isPlaying
        const savedTime = currentTime

        // Update state
        setAudioLanguageState(lang)
        setCurrentTrack(equivalentTrack)

        // If we're in a full playlist, update the playlist too
        if (playlist.length > 1) {
          setPlaylist(newPlaylist)
        } else {
          setPlaylist([equivalentTrack])
        }

        // Load and seek to the saved position
        audio.src = equivalentTrack.audioUrl
        audio.load()

        // Wait for load then seek and optionally resume
        const handleCanPlay = () => {
          audio.currentTime = savedTime
          if (wasPlaying) {
            audio.play()
          }
          audio.removeEventListener('canplay', handleCanPlay)
        }
        audio.addEventListener('canplay', handleCanPlay)
      }
    } else {
      // No current track, just update language state
      setAudioLanguageState(lang)
    }
  }, [currentTrack, currentTime, isPlaying, playlist.length])

  // Read language control - persisted to localStorage
  const setReadLanguage = useCallback((lang: AudioLanguage) => {
    persistReadLanguage(lang)
    setReadLanguageState(lang)
  }, [])

  // MediaSession API: update lock-screen metadata when track changes
  useEffect(() => {
    if (!currentTrack || typeof navigator === 'undefined' || !('mediaSession' in navigator)) return
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title,
      artist: 'LUKHAS Constellation Podcast',
      album: 'The Constellation',
      artwork: [
        {
          src: `/assets/icons/podcast/ep-${currentTrack.star ?? currentTrack.id}.svg`,
          sizes: 'any',
          type: 'image/svg+xml',
        },
      ],
    })
  }, [currentTrack])

  // MediaSession API: wire action handlers (re-wire when prev/next callbacks change)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return
    const audio = audioRef.current
    navigator.mediaSession.setActionHandler('play', () => audio?.play())
    navigator.mediaSession.setActionHandler('pause', () => audio?.pause())
    navigator.mediaSession.setActionHandler('nexttrack', nextTrack)
    navigator.mediaSession.setActionHandler('previoustrack', previousTrack)
    navigator.mediaSession.setActionHandler('seekbackward', (d) => {
      const offset = d.seekOffset ?? 15
      if (audio) audio.currentTime = Math.max(0, audio.currentTime - offset)
    })
    navigator.mediaSession.setActionHandler('seekforward', (d) => {
      const offset = d.seekOffset ?? 15
      if (audio) audio.currentTime = Math.min(audio.duration || Infinity, audio.currentTime + offset)
    })
    return () => {
      const actions = ['play', 'pause', 'nexttrack', 'previoustrack', 'seekbackward', 'seekforward'] as const
      actions.forEach((action) => {
        try { navigator.mediaSession.setActionHandler(action, null) } catch { /* not supported */ }
      })
    }
  }, [nextTrack, previousTrack])

  const value: AudioPlayerContextValue = {
    // State
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackSpeed,
    isLoaded,
    currentTrack,
    playlist,
    currentIndex,
    audioLanguage,
    readLanguage,
    isFloatingVisible,
    isMinimized,
    subtitleLanguage,
    currentSubtitle,

    // Controls
    play,
    pause,
    togglePlay,
    seek,
    skipForward,
    skipBackward,
    setVolume,
    setPlaybackSpeed,

    // Playlist controls
    loadTrack,
    playTrack,
    playPlaylist,
    nextTrack,
    previousTrack,
    playConstellation,

    // Preview controls
    previewTrack,
    stopPreview,
    isPreviewing,
    previewingTrackId,

    // UI controls
    showFloating,
    hideFloating,
    toggleMinimized,
    setSubtitleLanguage,

    // Language controls
    setAudioLanguage,
    setReadLanguage,

    // Ref
    audioRef,
  }

  return (
    <AudioPlayerContext.Provider value={value}>
      {/* Global audio element */}
      <audio ref={audioRef} preload="metadata" />
      {children}
    </AudioPlayerContext.Provider>
  )
}
